import React, { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { API_WEB_URLS } from "../../constants/constAPI";
import ExcelJS from "exceljs";
import useSession from "../utils/useSession";

// Native browser download helper (no file-saver dependency needed)
const saveAs = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 300);
};

// All import-able flat fields (for template + import validation)
const IMPORT_FLAT_FIELDS = [
  { key: "CardNo", label: "Employee Code" },
  { key: "Name", label: "Employee Name", required: true },
  { key: "FatherName", label: "Father Name" },
  { key: "SerialNo", label: "Serial No" },
  { key: "Sex", label: "Gender (M/F)" },
  { key: "DateOfBirth", label: "Date of Birth (YYYY-MM-DD)" },
  { key: "DateOfRetire", label: "Date of Retirement (YYYY-MM-DD)" },
  { key: "EmployeeType", label: "Employee Type" },
  { key: "Status", label: "Status" },
  { key: "Married", label: "Marital Status" },
  { key: "EducationQualification", label: "Education Qualification" },
  { key: "WeeklyHoliday", label: "Weekly Holiday" },
  { key: "MobileNo", label: "Mobile No" },
  { key: "Email", label: "Email" },
  { key: "PermanentAddress", label: "Permanent Address" },
  { key: "PresentAddress", label: "Present Address" },
  { key: "Remark", label: "Remark" },
  // Department & Shift
  { key: "DepartmentName", label: "Department Name" },
  { key: "DesignationName", label: "Designation Name" },
  { key: "Dept_EffectiveDate", label: "Dept Effective Date (YYYY-MM-DD)" },
  { key: "ShiftName", label: "Shift Name" },
  { key: "Shift_EffectiveFrom", label: "Shift Effective From (YYYY-MM-DD)" },
  // Join & Salary
  { key: "JoinDate", label: "Join Date (YYYY-MM-DD)" },
  { key: "JoinTypeName", label: "Join Type Name" },
  { key: "Sal_BasicSalary", label: "Basic Salary" },
  { key: "Sal_BasicTypeName", label: "Basic Type Name" },
  { key: "Sal_EffectiveDate", label: "Salary Effective Date (YYYY-MM-DD)" },
  // ESI
  { key: "ESIApplicable", label: "ESI Applicable (YES/NO)" },
  { key: "ESINo", label: "ESI Number" },
  { key: "ESIJoiningDate", label: "ESI Joining Date (YYYY-MM-DD)" },
  { key: "ESIOldEmployee", label: "ESI Old Employee (YES/NO)" },
  { key: "TICs", label: "TICs (YES/NO)" },
  { key: "PICs", label: "PICs (YES/NO)" },
  { key: "Accident", label: "Accident (YES/NO)" },
  // PF
  { key: "PFApplicable", label: "PF Applicable (YES/NO)" },
  { key: "PFNo", label: "PF Number" },
  { key: "UAN", label: "UAN" },
  { key: "PFJoinDate", label: "PF Join Date (YYYY-MM-DD)" },
  { key: "PensionApplicable", label: "Pension Applicable (YES/NO)" },
  { key: "ExtraPF", label: "Extra PF" },
  { key: "PFCalculationType", label: "PF Calculation Type (P/F/M)" },
  { key: "EmplrPFOverLimit", label: "Emplr PF Over Limit (YES/NO)" },
  { key: "EmpleeDedOverLimit", label: "Emplee Ded Over Limit (YES/NO)" },
  { key: "PensionOverLimit", label: "Pension Over Limit (YES/NO)" },
  { key: "HigherPension", label: "Higher Pension (YES/NO)" },
  { key: "FixedPFSalary", label: "Fixed PF Salary" },
  { key: "OldMemberPF", label: "Old Member PF (YES/NO)" },
  { key: "OldMemberPension", label: "Old Member Pension (YES/NO)" },
  { key: "PrevPFNo", label: "Previous PF No" },
  { key: "OldUAN", label: "Old UAN" },
  { key: "DateOfExitPr", label: "Date Of Exit Prev (YYYY-MM-DD)" },
  { key: "PMRPY", label: "PMRPY (YES/NO)" },
  { key: "PMGKY", label: "PMGKY (YES/NO)" },
  { key: "ABRY", label: "ABRY (YES/NO)" },
  { key: "IsDeferredPension", label: "Is Deferred Pension (YES/NO)" },
  { key: "PensionAge", label: "Pension Age" },
  // Bank & KYC
  { key: "BankName", label: "Bank Name" },
  { key: "BankBranch", label: "Bank Branch" },
  { key: "IFSCCode", label: "IFSC Code" },
  { key: "BankAccountNo", label: "Bank Account No" },
  { key: "NameAsBank", label: "Name As Per Bank" },
  { key: "BankPayment", label: "Bank Payment Mode (YES/NO)" },
  { key: "PanCardNo", label: "PAN Card No" },
  { key: "NameAsPerPan", label: "Name As Per PAN" },
  { key: "AadharNumber", label: "Aadhar Number" },
  { key: "NameInAadhar", label: "Name In Aadhar" },
  { key: "DrivingLicenceNo", label: "Driving Licence No" },
  { key: "DLExpiryDate", label: "DL Expiry Date (YYYY-MM-DD)" },
  { key: "NameAsPerDL", label: "Name As Per DL" },
  { key: "PassportNo", label: "Passport No" },
  { key: "PassportFrom", label: "Passport Valid From (YYYY-MM-DD)" },
  { key: "PassportTill", label: "Passport Valid Till (YYYY-MM-DD)" },
  { key: "PassportName", label: "Name As Per Passport" },
  { key: "IsInternationalEmployee", label: "Is International Employee (YES/NO)" },
  { key: "CountryOrigin", label: "Country Origin" },
  { key: "Nationality", label: "Nationality" },
  { key: "VoterIDNo", label: "Voter ID No" },
  { key: "RationCardNo", label: "Ration Card No" },
];

// Reference guidelines & format definitions for the helper modal
const FORMAT_GUIDELINES = [
  {
    category: "🔑 Core Identification & Matching",
    fields: [
      {
        field: "Employee Code (CardNo)",
        format: "Text / Number",
        allowed: "Any Unique Code (e.g. 0001, EMP-101)",
        rule: "PRIMARY MATCHING KEY. If found in active firm → UPDATES employee. If blank or not found → INSERTS new employee.",
        example: "0001 or EMP-101",
      },
      {
        field: "Employee Name (Name)",
        format: "Text (Required)",
        allowed: "Alphabetical Full Name",
        rule: "Mandatory for all employees. Rows without a Name will be skipped with an error.",
        example: "Rajesh Sharma",
      },
      {
        field: "Gender / Sex",
        format: "Standard Code",
        allowed: "M, F, O (or Male, Female, Other)",
        rule: "Exported as M or F. Standard code M = Male, F = Female, O = Other.",
        example: "M",
      },
    ],
  },
  {
    category: "⚙️ Value Codes & Calculation Types",
    fields: [
      {
        field: "Calculation Type (Allowances / Deductions / PF)",
        format: "Single Letter Code",
        allowed: "M, P, D, F (or Manual, Monthly, Percentage, Daily, Fixed)",
        rule: "Standard single-letter code:\n• M = Monthly / Manual Amount\n• P = Percentage (%)\n• D = Daily Calculation\n• F = Fixed Rate",
        example: "M (for Fixed Monthly DA), P (for 12% PF)",
      },
      {
        field: "Boolean / Flag Fields (All Yes/No)",
        format: "YES / NO",
        allowed: "YES, NO, Y, N, 1, 0, TRUE, FALSE",
        rule: "All Yes/No flags (PFApplicable, ESIApplicable, IsOnGross, IsApplicable, BankPayment, etc.) are exported as YES or NO. Case-insensitive on import.",
        example: "YES",
      },
      {
        field: "Date Fields (All Dates)",
        format: "YYYY-MM-DD",
        allowed: "YYYY-MM-DD (ISO Format)",
        rule: "All dates (DateOfBirth, PFJoinDate, ESIJoiningDate, EffectiveDates, ExpiryDates) must follow YYYY-MM-DD.",
        example: "1994-06-15",
      },
    ],
  },
  {
    category: "🏢 History & Foreign Key Masters",
    fields: [
      {
        field: "Department & Designation",
        format: "Name or ID",
        allowed: "Department Name, Designation Name",
        rule: "Automatically resolves to the Department and Designation Master IDs in the active firm and creates/updates the latest history record.",
        example: "Production, Supervisor",
      },
      {
        field: "Shift Master",
        format: "Name or ID",
        allowed: "Shift Name (e.g. General, Day, Night)",
        rule: "Resolves to ShiftMaster and updates the latest Shift history with Shift_EffectiveFrom date.",
        example: "General Shift",
      },
      {
        field: "Join & Salary History",
        format: "Values & Dates",
        allowed: "JoinDate, BasicSalary, BasicType",
        rule: "Updates the latest Join history and Salary history records with effective date tracking.",
        example: "JoinDate: 2023-01-01, Sal_BasicSalary: 18000",
      },
    ],
  },
  {
    category: "🎁 Dynamic Allowances & Deductions",
    fields: [
      {
        field: "Allow_<Id>_Amount / Ded_<Id>_Amount",
        format: "Numeric",
        allowed: "Decimal amount (e.g. 500.00)",
        rule: "Specified amount or percentage rate for the dynamic allowance/deduction.",
        example: "1200.00",
      },
      {
        field: "Allow_<Id>_Calculation / Ded_<Id>_Calculation",
        format: "Code (M/P/D/F)",
        allowed: "M = Monthly/Manual, P = Percentage, D = Daily, F = Fixed",
        rule: "Defines how the dynamic allowance/deduction will be calculated in payroll processing.",
        example: "M",
      },
      {
        field: "Allow_<Id>_IsApplicable, IsOnGross, IsPFApply...",
        format: "YES / NO",
        allowed: "YES or NO",
        rule: "Controls if the allowance is active, added to Gross, subject to PF/ESI, or linked to working days.",
        example: "YES",
      },
    ],
  },
];

// Helper: format boolean for display / export
const fmtBool = (v) => {
  if (v === true || v === 1 || v === "1" || String(v).toUpperCase() === "TRUE" || String(v).toUpperCase() === "YES" || String(v).toUpperCase() === "Y") {
    return "YES";
  }
  if (v === false || v === 0 || v === "0" || String(v).toUpperCase() === "FALSE" || String(v).toUpperCase() === "NO" || String(v).toUpperCase() === "N") {
    return "NO";
  }
  return v ?? "";
};

// Helper: parse bool from Excel / input
const parseBool = (v) => {
  if (v === true || v === 1 || v === "1") return true;
  const s = String(v).trim().toUpperCase();
  if (s === "TRUE" || s === "YES" || s === "Y") return true;
  return false;
};

// Helper: safe date string (YYYY-MM-DD)
const safeDate = (v) => {
  if (!v) return "";
  if (v instanceof Date) {
    const yyyy = v.getFullYear();
    const mm = String(v.getMonth() + 1).padStart(2, "0");
    const dd = String(v.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }
  const s = String(v).trim();
  return s.substring(0, 10);
};

// Helper: normalize calculation code (M, P, D, F)
const normalizeCalcCode = (v) => {
  if (!v) return "M";
  const s = String(v).trim().toUpperCase();
  const first = s.charAt(0);
  if (first === "P" || s === "%") return "P";
  if (first === "D") return "D";
  if (first === "F") return "F";
  if (first === "M") return "M";
  return "M";
};

// Helper: normalize gender (M, F, O)
const normalizeGender = (v) => {
  if (!v) return "M";
  const s = String(v).trim().toUpperCase();
  if (s === "M" || s === "MALE" || s === "1") return "M";
  if (s === "F" || s === "FEMALE" || s === "2") return "F";
  if (s === "O" || s === "OTHER" || s === "OTHERS") return "O";
  return s.charAt(0);
};

// ─────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────
const EmployeeImportExport = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Export state
  const [exporting, setExporting] = useState(false);
  const [exportColumns, setExportColumns] = useState([]);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);
  const [exportSummary, setExportSummary] = useState({
    totalEmployees: 0,
    totalColumns: 0,
    allowancesCount: 0,
    deductionsCount: 0,
    loading: false,
  });

  // Import state
  const [importFile, setImportFile] = useState(null);
  const [previewData, setPreviewData] = useState([]); // { rowIndex, data, errors[], status, isNew }
  const [importProgress, setImportProgress] = useState(0);
  const [importing, setImporting] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [importResults, setImportResults] = useState({
    total: 0,
    success: 0,
    inserted: 0,
    updated: 0,
    failed: 0,
    errors: [],
    details: [],
  });
  const [isParsing, setIsParsing] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  // Guidelines modal state
  const [showGuidelinesModal, setShowGuidelinesModal] = useState(false);
  const [guidelineSearch, setGuidelineSearch] = useState("");

  // Firm from Session
  const { firm: selectedFirm, firmId: sessionFirmId } = useSession();
  const firmId = sessionFirmId ? String(sessionFirmId) : "0";

  const fileInputRef = useRef(null);

  // Auth token
  const authUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("userDetails") || "{}");
    } catch {
      return {};
    }
  })();
  const authToken = authUser?.token || API_WEB_URLS.Token;
  const userId = authUser?.Id ? String(authUser.Id) : "1";

  // ──────────────────────────────────────────
  // Fetch Export Stats on Mount
  // ──────────────────────────────────────────
  const loadExportSummary = async () => {
    if (!firmId || firmId === "0") return;
    setExportSummary((prev) => ({ ...prev, loading: true }));
    try {
      const res = await fetch(`${API_WEB_URLS.BASE}EmployeeExport/0/token/${firmId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.ok) {
        const json = await res.json();
        const exportData = json?.data;
        if (exportData) {
          setExportColumns(exportData.columns || []);
          setExportSummary({
            totalEmployees: exportData.totalCount || exportData.rows?.length || 0,
            totalColumns: exportData.columns?.length || 0,
            allowancesCount: exportData.allowances?.length || 0,
            deductionsCount: exportData.deductions?.length || 0,
            loading: false,
          });
        }
      }
    } catch (err) {
      console.error("Failed to load export summary:", err);
    } finally {
      setExportSummary((prev) => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    loadExportSummary();
  }, [firmId]);

  // ──────────────────────────────────────────
  // Direct Export to Excel (All Dynamic Data)
  // ──────────────────────────────────────────
  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await fetch(`${API_WEB_URLS.BASE}EmployeeExport/0/token/${firmId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      if (!res.ok) {
        throw new Error(`API returned HTTP ${res.status}`);
      }

      const json = await res.json();
      const exportData = json?.data;
      const employees = exportData?.rows || [];
      const columns = exportData?.columns || [];

      if (!employees || employees.length === 0) {
        toast.warning("No employee data found to export.");
        setExporting(false);
        return;
      }

      if (!columns || columns.length === 0) {
        toast.warning("No export columns available.");
        setExporting(false);
        return;
      }

      // Build workbook
      const workbook = new ExcelJS.Workbook();
      workbook.creator = "Payroll System";
      workbook.created = new Date();

      const firmName = selectedFirm?.FirmName || selectedFirm?.Name
        ? (selectedFirm.FirmName || selectedFirm.Name).replace(/[^a-zA-Z0-9_-]/g, "_")
        : "Employees";
      const sheet = workbook.addWorksheet("Employees", {
        views: [{ state: "frozen", ySplit: 1 }],
      });

      // Column definitions directly as returned by API
      sheet.columns = columns.map((col) => ({
        header: col.label,
        key: col.key,
        width: Math.max(col.label.length + 4, 18),
      }));

      // Style header row
      const headerRow = sheet.getRow(1);
      headerRow.eachCell((cell) => {
        cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF4F46E5" } };
        cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 11, name: "Calibri" };
        cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
        cell.border = {
          top: { style: "thin", color: { argb: "FFCCCCCC" } },
          left: { style: "thin", color: { argb: "FFCCCCCC" } },
          bottom: { style: "thin", color: { argb: "FFCCCCCC" } },
          right: { style: "thin", color: { argb: "FFCCCCCC" } },
        };
      });
      headerRow.height = 32;

      // Add data rows
      employees.forEach((emp, idx) => {
        const rowData = {};
        columns.forEach((col) => {
          let val = emp[col.key];

          // Format booleans (YES / NO)
          if (
            col.type === "boolean" ||
            [
              "PFApplicable", "ESIApplicable", "PensionApplicable", "IsDeferredPension",
              "EmplrPFOverLimit", "EmpleeDedOverLimit", "PensionOverLimit", "HigherPension",
              "OldMemberPF", "OldMemberPension", "PMRPY", "PMGKY", "ABRY",
              "BankPayment", "IsHandicap", "Refunded", "TICs", "PICs", "Accident",
              "ESIOldEmployee", "IsInternationalEmployee",
            ].includes(col.key) ||
            col.key.endsWith("_IsOnGross") ||
            col.key.endsWith("_IsPFApply") ||
            col.key.endsWith("_IsESIApply") ||
            col.key.endsWith("_IsRoundOffApply") ||
            col.key.endsWith("_IsWorkingDaysApply") ||
            col.key.endsWith("_IsApplicable")
          ) {
            if (val !== null && val !== undefined && val !== "") {
              val = fmtBool(val);
            } else {
              val = "";
            }
          }
          // Format gender (M / F)
          else if (col.key === "Sex") {
            val = normalizeGender(val);
          }
          // Format calculation type (M / P / D / F)
          else if (col.key === "PFCalculationType" || col.key.endsWith("_Calculation")) {
            val = normalizeCalcCode(val);
          }
          // Format dates (YYYY-MM-DD)
          else if (
            col.type === "date" ||
            col.key.includes("Date") ||
            col.key.includes("From") ||
            col.key.includes("Till") ||
            col.key.endsWith("_EffectiveFrom")
          ) {
            val = safeDate(val);
          }

          rowData[col.key] = val ?? "";
        });
        const row = sheet.addRow(rowData);

        // Alternating row colors
        const bgColor = idx % 2 === 0 ? "FFFFFFFF" : "FFF9FAFB";
        row.eachCell((cell) => {
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bgColor } };
          cell.alignment = { vertical: "middle", horizontal: "left" };
          cell.border = {
            top: { style: "thin", color: { argb: "FFE5E7EB" } },
            left: { style: "thin", color: { argb: "FFE5E7EB" } },
            bottom: { style: "thin", color: { argb: "FFE5E7EB" } },
            right: { style: "thin", color: { argb: "FFE5E7EB" } },
          };
        });
        row.height = 22;
      });

      // Auto-fit columns (max 50)
      sheet.columns.forEach((col) => {
        let maxLen = col.header ? col.header.length : 10;
        col.eachCell({ includeEmpty: false }, (cell) => {
          const len = cell.value ? String(cell.value).length : 0;
          if (len > maxLen) maxLen = len;
        });
        col.width = Math.min(Math.max(maxLen + 4, 12), 50);
      });

      // Save file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const dateStr = new Date().toISOString().slice(0, 10);
      saveAs(blob, `Employees_Export_${firmName}_${dateStr}.xlsx`);

      toast.success(`✅ Exported ${employees.length} employees with ${columns.length} columns successfully!`);
    } catch (err) {
      console.error("Export error:", err);
      toast.error("Export failed. Please check network/API.");
    } finally {
      setExporting(false);
    }
  };

  // ──────────────────────────────────────────
  // Template Download (Dynamic Headings, No Data Rows)
  // ──────────────────────────────────────────
  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      let columns = exportColumns;

      // If columns not yet in state and firmId is selected, fetch fresh columns from export API for this firm
      if ((!columns || columns.length === 0) && firmId && firmId !== "0") {
        try {
          const res = await fetch(`${API_WEB_URLS.BASE}EmployeeExport/0/token/${firmId}`, {
            headers: { Authorization: `Bearer ${authToken}` },
          });
          if (res.ok) {
            const json = await res.json();
            if (json?.data?.columns && json.data.columns.length > 0) {
              columns = json.data.columns;
              setExportColumns(columns);
            }
          }
        } catch (e) {
          console.warn("Could not fetch firm export columns for template, using default fields:", e);
        }
      }

      const workbook = new ExcelJS.Workbook();
      workbook.creator = "Payroll System";
      workbook.created = new Date();

      const sheet = workbook.addWorksheet("Employee Template", {
        views: [{ state: "frozen", ySplit: 1 }],
      });

      if (columns && columns.length > 0) {
        sheet.columns = columns.map((col) => ({
          header: col.label,
          key: col.key,
          width: Math.max(col.label.length + 4, 18),
        }));
      } else {
        sheet.columns = IMPORT_FLAT_FIELDS.map((f) => ({
          header: f.label,
          key: f.key,
          width: Math.max(f.label.length + 4, 22),
        }));
      }

      // Style header row
      const headerRow = sheet.getRow(1);
      headerRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FF1D4ED8" }, // Clean Professional Blue
        };
        cell.font = { bold: true, color: { argb: "FFFFFFFF" }, size: 11, name: "Calibri" };
        cell.alignment = { vertical: "middle", horizontal: "center", wrapText: true };
        cell.border = {
          top: { style: "thin", color: { argb: "FFCCCCCC" } },
          left: { style: "thin", color: { argb: "FFCCCCCC" } },
          bottom: { style: "thin", color: { argb: "FFCCCCCC" } },
          right: { style: "thin", color: { argb: "FFCCCCCC" } },
        };
      });
      headerRow.height = 32;

      // Note: NO data rows or sample rows are added - ONLY headings as requested!

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const firmName = selectedFirm?.FirmName || selectedFirm?.Name
        ? (selectedFirm.FirmName || selectedFirm.Name).replace(/[^a-zA-Z0-9_-]/g, "_")
        : "Firm";

      saveAs(blob, `Employee_Import_Template_${firmName}.xlsx`);
      toast.success("📄 Template downloaded with only column headings (ready for data entry)!");
    } catch (err) {
      console.error("Template download error:", err);
      toast.error("Failed to download template.");
    } finally {
      setDownloadingTemplate(false);
    }
  };

  // ──────────────────────────────────────────
  // Import - Parse Excel File
  // ──────────────────────────────────────────
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const ext = file.name.split(".").pop().toLowerCase();
    if (!["xlsx", "xls"].includes(ext)) {
      toast.error("Invalid file! Please upload an Excel file (.xlsx or .xls)");
      e.target.value = "";
      return;
    }

    setImportFile(file);
    setIsParsing(true);
    setPreviewData([]);
    setShowPreview(false);

    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = new ExcelJS.Workbook();
      await workbook.xlsx.load(arrayBuffer);

      const sheet = workbook.worksheets[0];
      if (!sheet) {
        toast.error("No sheet found in the Excel file.");
        setIsParsing(false);
        return;
      }

      // Get headers from first row
      const headers = [];
      sheet.getRow(1).eachCell((cell, colNum) => {
        headers[colNum - 1] = String(cell.value || "").trim();
      });

      // Map labels and direct keys
      const labelToKey = {};
      IMPORT_FLAT_FIELDS.forEach((f) => {
        labelToKey[f.label] = f.key;
        labelToKey[f.key] = f.key;
      });

      // Also map export column labels if available
      if (exportColumns && exportColumns.length > 0) {
        exportColumns.forEach((c) => {
          if (c.label) labelToKey[c.label] = c.key;
          if (c.key) labelToKey[c.key] = c.key;
        });
      }
      const rows = [];
      sheet.eachRow((row, rowNumber) => {
        if (rowNumber === 1) return; // skip header row

        const obj = {};
        const allowancesMap = {};
        const deductionsMap = {};

        row.eachCell({ includeEmpty: true }, (cell, colNum) => {
          const headerName = headers[colNum - 1];
          if (!headerName) return;

          let mappedKey = labelToKey[headerName] || headerName;
          let val = cell.value;
          if (val && typeof val === "object" && val.result !== undefined) val = val.result;
          if (val instanceof Date) val = safeDate(val);
          const valStr = val !== null && val !== undefined ? String(val).trim() : "";

          // Check if dynamic allowance column (e.g. Allow_1_Amount, Allow_1_Calculation)
          const allowMatch = mappedKey.match(/^Allow_(\d+)_(.+)$/);
          if (allowMatch) {
            const allowId = allowMatch[1];
            const prop = allowMatch[2];
            if (!allowancesMap[allowId]) allowancesMap[allowId] = { F_AllowanceMaster: Number(allowId) };
            allowancesMap[allowId][prop] = valStr;
            return;
          }

          // Check if dynamic deduction column (e.g. Ded_1_Amount, Ded_1_Calculation)
          const dedMatch = mappedKey.match(/^Ded_(\d+)_(.+)$/);
          if (dedMatch) {
            const dedId = dedMatch[1];
            const prop = dedMatch[2];
            if (!deductionsMap[dedId]) deductionsMap[dedId] = { F_DeductionMaster: Number(dedId) };
            deductionsMap[dedId][prop] = valStr;
            return;
          }

          obj[mappedKey] = valStr;
        });

        // Skip completely empty rows
        const hasData = Object.values(obj).some((v) => v && String(v).trim() !== "") ||
          Object.keys(allowancesMap).length > 0;
        if (!hasData) return;

        // Attach structured allowances & deductions
        obj.Allowances = Object.values(allowancesMap);
        obj.Deductions = Object.values(deductionsMap);

        // Normalize Standard Values
        if (obj.Sex) obj.Sex = normalizeGender(obj.Sex);
        if (obj.PFCalculationType) obj.PFCalculationType = normalizeCalcCode(obj.PFCalculationType);
        if (obj.Dept_EffectiveDate && (!obj.ESIJoiningDate || String(obj.ESIJoiningDate).trim() === "")) {
          obj.ESIJoiningDate = obj.Dept_EffectiveDate;
        }
        if (obj.Dept_EffectiveDate && (!obj.PFJoinDate || String(obj.PFJoinDate).trim() === "")) {
          obj.PFJoinDate = obj.Dept_EffectiveDate;
        }
        if (parseBool(obj.PFApplicable) && (obj.PensionApplicable === undefined || obj.PensionApplicable === null || String(obj.PensionApplicable).trim() === "")) {
          obj.PensionApplicable = "YES";
        }

        // Required field validation
        const errors = [];
        if (!obj.Name || String(obj.Name).trim() === "") {
          errors.push("Employee Name is required");
        }

        // Validate mobile
        if (obj.MobileNo && !/^\d{0,12}$/.test(obj.MobileNo.replace(/[-+\s]/g, ""))) {
          errors.push("Mobile No format invalid");
        }

        const isNewRecord = !obj.CardNo || String(obj.CardNo).trim() === "";

        rows.push({
          rowIndex: rowNumber,
          data: obj,
          errors,
          status: errors.length > 0 ? "error" : "pending",
          isNew: isNewRecord,
        });
      });

      if (rows.length === 0) {
        toast.warning("No data rows found in the Excel file.");
        setIsParsing(false);
        return;
      }

      setPreviewData(rows);
      setShowPreview(true);
      toast.success(`📊 Parsed ${rows.length} rows. Review the data and click "Run Import".`);
    } catch (err) {
      console.error("Parse error:", err);
      toast.error("Failed to parse Excel file. Please check the file structure.");
    } finally {
      setIsParsing(false);
    }
  };

  // ──────────────────────────────────────────
  // Import - Unified Auto Insert / Update
  // ──────────────────────────────────────────
  const handleImport = async () => {
    const validRows = previewData.filter((r) => r.status !== "error");
    if (validRows.length === 0) {
      toast.warning("No valid rows to import. Please fix errors first.");
      return;
    }

    setImporting(true);
    setImportProgress(20);

    try {
      // Prepare clean payload for batch API
      const employeePayload = validRows.map((r) => r.data);

      setImportProgress(50);

      const res = await fetch(
        `${API_WEB_URLS.BASE}EmployeeImport/${userId || 0}/token/${firmId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
          body: JSON.stringify(employeePayload),
        }
      );

      setImportProgress(85);

      if (!res.ok) {
        const errJson = await res.json().catch(() => null);
        throw new Error(errJson?.message || `API returned HTTP ${res.status}`);
      }

      const json = await res.json();
      const summary = json?.data?.summary || {};
      const rowResults = json?.data?.results || [];

      // Update preview data statuses
      const updated = previewData.map((rowItem) => {
        if (rowItem.status === "error") return { ...rowItem, status: "skipped" };
        const match = rowResults.find(
          (r) =>
            (r.CardNo && r.CardNo === rowItem.data.CardNo) ||
            r.Name === rowItem.data.Name
        );
        if (match) {
          return {
            ...rowItem,
            status: match.Status === "SUCCESS" ? "success" : "failed",
            apiAction: match.Action,
            apiError: match.Status !== "SUCCESS" ? match.Message : null,
          };
        }
        return { ...rowItem, status: "success" };
      });

      setPreviewData(updated);
      setImportProgress(100);

      const inserted = summary.InsertedCount || 0;
      const updatedCount = summary.UpdatedCount || 0;
      const failed = summary.FailedCount || 0;
      const total = summary.TotalRows || validRows.length;
      const success = summary.SuccessCount || inserted + updatedCount;

      const errors = rowResults
        .filter((r) => r.Status !== "SUCCESS")
        .map((r) => `Row ${r.RowIndex} (${r.CardNo || r.Name}): ${r.Message}`);

      setImportResults({
        total,
        success,
        inserted,
        updated: updatedCount,
        failed,
        errors,
        details: rowResults,
      });

      setShowResultModal(true);
      loadExportSummary(); // refresh stats

      if (success > 0) {
        toast.success(
          `🎉 Import Complete! ${inserted} inserted, ${updatedCount} updated successfully.`
        );
      }
      if (failed > 0) {
        toast.error(`❌ ${failed} records failed during import.`);
      }
    } catch (err) {
      console.error("Import error:", err);
      toast.error(`Import failed: ${err.message}`);
    } finally {
      setImporting(false);
    }
  };

  // ──────────────────────────────────────────
  // Reset Import
  // ──────────────────────────────────────────
  const resetImport = () => {
    setImportFile(null);
    setPreviewData([]);
    setShowPreview(false);
    setImportProgress(0);
    setImporting(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ──────────────────────────────────────────
  // UI Helpers
  // ──────────────────────────────────────────
  const StatusBadge = ({ status, action }) => {
    const map = {
      pending: { bg: "#6c757d", label: "Pending" },
      success: {
        bg: action === "INSERTED" ? "#059669" : "#2563eb",
        label: action === "INSERTED" ? "✓ Inserted" : "✓ Updated",
      },
      failed: { bg: "#dc3545", label: "✗ Failed" },
      skipped: { bg: "#fd7e14", label: "⚠ Skipped" },
      error: { bg: "#dc3545", label: "✗ Invalid" },
    };
    const info = map[status] || map["pending"];
    return (
      <span
        style={{
          background: info.bg,
          color: "#fff",
          borderRadius: 4,
          padding: "2px 8px",
          fontSize: 11,
          fontWeight: 600,
          whiteSpace: "nowrap",
        }}
      >
        {info.label}
      </span>
    );
  };

  const validRows = previewData.filter((r) => r.status !== "error").length;
  const invalidRows = previewData.filter((r) => r.status === "error").length;

  // Filtered guidelines for search
  const filteredGuidelines = FORMAT_GUIDELINES.map((cat) => ({
    ...cat,
    fields: cat.fields.filter(
      (f) =>
        f.field.toLowerCase().includes(guidelineSearch.toLowerCase()) ||
        f.format.toLowerCase().includes(guidelineSearch.toLowerCase()) ||
        f.allowed.toLowerCase().includes(guidelineSearch.toLowerCase()) ||
        f.rule.toLowerCase().includes(guidelineSearch.toLowerCase())
    ),
  })).filter((cat) => cat.fields.length > 0);

  // ──────────────────────────────────────────
  // Render
  // ──────────────────────────────────────────
  return (
    <div style={{ padding: "20px 24px", fontFamily: "'Poppins', sans-serif" }}>
      {/* Page Header */}
      <div
        style={{
          background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
          borderRadius: 16,
          padding: "28px 32px",
          marginBottom: 28,
          color: "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          boxShadow: "0 8px 32px rgba(79,70,229,0.25)",
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontWeight: 700, fontSize: 26, letterSpacing: -0.5 }}>
            🔄 Employee Import / Export
          </h2>
          <p style={{ margin: "6px 0 0", opacity: 0.9, fontSize: 14 }}>
            Direct 1-click Excel Export & Unified Auto Insert / Update Employee Import
          </p>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            className="btn"
            style={{
              background: "#fff",
              color: "#4f46e5",
              fontWeight: 700,
              borderRadius: 8,
              padding: "10px 18px",
              fontSize: 13,
              cursor: "pointer",
              border: "none",
              boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
            onClick={() => setShowGuidelinesModal(true)}
          >
            📖 Format Guidelines & Codes
          </button>
          <button
            className="btn"
            style={{
              background: "rgba(255,255,255,0.15)",
              border: "1px solid rgba(255,255,255,0.4)",
              color: "#fff",
              borderRadius: 8,
              padding: "10px 18px",
              fontSize: 13,
              cursor: "pointer",
            }}
            onClick={() => navigate("/EmployeeMaster")}
          >
            ← Back to Employee List
          </button>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
        {/* ── EXPORT CARD ── */}
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
            overflow: "hidden",
            border: "1px solid #e8e8f0",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              background: "linear-gradient(135deg, #059669, #10b981)",
              padding: "20px 24px",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div>
              <h4 style={{ margin: 0, fontWeight: 700, fontSize: 18 }}>
                📤 Export Employees
              </h4>
              <p style={{ margin: "4px 0 0", opacity: 0.9, fontSize: 13 }}>
                {selectedFirm?.FirmName || selectedFirm?.Name || "Selected Firm"}
              </p>
            </div>
            {exportSummary.totalEmployees > 0 && (
              <span
                style={{
                  background: "rgba(255,255,255,0.22)",
                  color: "#fff",
                  borderRadius: 20,
                  padding: "4px 12px",
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {exportSummary.totalEmployees} Employees
              </span>
            )}
          </div>
          <div style={{ padding: 24, flex: 1, display: "flex", flexDirection: "column" }}>
            {/* Dynamic Summary Cards */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 10,
                marginBottom: 18,
              }}
            >
              <div
                style={{
                  background: "#f0fdf4",
                  border: "1px solid #bbf7d0",
                  borderRadius: 10,
                  padding: "10px 14px",
                }}
              >
                <div style={{ fontSize: 11, color: "#166534", fontWeight: 600, textTransform: "uppercase" }}>
                  Total Employees
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, color: "#059669" }}>
                  {exportSummary.loading ? "..." : exportSummary.totalEmployees}
                </div>
              </div>
              <div
                style={{
                  background: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  borderRadius: 10,
                  padding: "10px 14px",
                }}
              >
                <div style={{ fontSize: 11, color: "#1e40af", fontWeight: 600, textTransform: "uppercase" }}>
                  Exportable Fields
                </div>
                <div style={{ fontSize: 20, fontWeight: 700, color: "#2563eb" }}>
                  {exportSummary.loading ? "..." : exportSummary.totalColumns || "—"}
                </div>
              </div>
              <div
                style={{
                  background: "#fff7ed",
                  border: "1px solid #fed7aa",
                  borderRadius: 10,
                  padding: "10px 14px",
                }}
              >
                <div style={{ fontSize: 11, color: "#9a3412", fontWeight: 600, textTransform: "uppercase" }}>
                  Firm Allowances
                </div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#ea580c" }}>
                  {exportSummary.loading ? "..." : `${exportSummary.allowancesCount} Active`}
                </div>
              </div>
              <div
                style={{
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: 10,
                  padding: "10px 14px",
                }}
              >
                <div style={{ fontSize: 11, color: "#991b1b", fontWeight: 600, textTransform: "uppercase" }}>
                  Firm Deductions
                </div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#dc2626" }}>
                  {exportSummary.loading ? "..." : `${exportSummary.deductionsCount} Active`}
                </div>
              </div>
            </div>

            <div
              style={{
                background: "#fafafa",
                borderRadius: 10,
                padding: "12px 16px",
                marginBottom: 20,
                border: "1px solid #f0f0f5",
                fontSize: 13,
                color: "#4b5563",
                lineHeight: 1.6,
              }}
            >
              ✨ Includes latest effective records for <strong>Department</strong>, <strong>Shift</strong>, <strong>Join</strong>, and <strong>Salary</strong>, with standardized codes (<strong>M/P/D/F</strong>, <strong>YES/NO</strong>, <strong>M/F</strong>, <strong>YYYY-MM-DD</strong>).
            </div>

            <div style={{ marginTop: "auto" }}>
              <button
                className="btn"
                style={{
                  width: "100%",
                  background: "linear-gradient(135deg, #059669, #10b981)",
                  color: "#fff",
                  borderRadius: 10,
                  padding: "14px 20px",
                  fontWeight: 700,
                  fontSize: 15,
                  border: "none",
                  cursor: exporting ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                  boxShadow: "0 4px 16px rgba(5,150,105,0.35)",
                  transition: "all 0.2s",
                }}
                onClick={handleExport}
                disabled={exporting}
              >
                {exporting ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" />
                    Generating & Downloading Excel...
                  </>
                ) : (
                  "📥 Export & Download Excel"
                )}
              </button>
            </div>
          </div>
        </div>

        {/* ── IMPORT CARD (UNIFIED AUTO INSERT / UPDATE) ── */}
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
            overflow: "hidden",
            border: "1px solid #e8e8f0",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <div
            style={{
              background: "linear-gradient(135deg, #1d4ed8, #3b82f6)",
              padding: "20px 24px",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <div>
              <h4 style={{ margin: 0, fontWeight: 700, fontSize: 18 }}>
                📥 Import Employees (Auto Insert / Update)
              </h4>
              <p style={{ margin: "4px 0 0", opacity: 0.9, fontSize: 13 }}>
                Upload Excel file — matches by <strong>Employee Code (Card No)</strong>
              </p>
            </div>
            <button
              style={{
                background: "rgba(255,255,255,0.2)",
                border: "1px solid rgba(255,255,255,0.4)",
                color: "#fff",
                borderRadius: 6,
                padding: "6px 12px",
                fontSize: 12,
                cursor: "pointer",
                fontWeight: 600,
              }}
              onClick={() => setShowGuidelinesModal(true)}
            >
              ℹ️ Guidelines
            </button>
          </div>

          <div style={{ padding: 24, flex: 1, display: "flex", flexDirection: "column" }}>
            {/* Template download notice */}
            <div
              style={{
                background: "#f0fdf4",
                border: "1px solid #bbf7d0",
                borderRadius: 10,
                padding: "12px 16px",
                marginBottom: 16,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
              }}
            >
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, color: "#166534" }}>
                  💡 Need the template format?
                </div>
                <div style={{ fontSize: 12, color: "#15803d", marginTop: 2 }}>
                  Download the blank template with all firm column headings (no data rows).
                </div>
              </div>
              <button
                className="btn"
                style={{
                  background: "#16a34a",
                  color: "#fff",
                  border: "none",
                  borderRadius: 6,
                  padding: "6px 14px",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: downloadingTemplate ? "not-allowed" : "pointer",
                  whiteSpace: "nowrap",
                }}
                onClick={handleDownloadTemplate}
                disabled={downloadingTemplate}
              >
                {downloadingTemplate ? "⏳ Downloading..." : "📄 Download Template (Headings Only)"}
              </button>
            </div>

            {/* Matching explanation */}
            <div
              style={{
                background: "#eff6ff",
                border: "1px solid #bfdbfe",
                borderRadius: 10,
                padding: "10px 14px",
                marginBottom: 16,
                fontSize: 12,
                color: "#1e40af",
                lineHeight: 1.5,
              }}
            >
              <strong>⚡ Auto-Matching Rule:</strong>
              <ul style={{ margin: "4px 0 0", paddingLeft: 18 }}>
                <li><strong>Existing Employee:</strong> If <code>Employee Code</code> exists in active firm → <strong>Updates</strong> master, history & allowances.</li>
                <li><strong>New Employee:</strong> If <code>Employee Code</code> is empty or not in active firm → <strong>Inserts</strong> a new employee record.</li>
              </ul>
            </div>

            {/* File Dropzone */}
            <div
              style={{
                border: "2px dashed #cbd5e1",
                borderRadius: 12,
                padding: "24px 20px",
                textAlign: "center",
                background: "#f8fafc",
                cursor: "pointer",
                marginBottom: 16,
                transition: "border-color 0.2s",
              }}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const file = e.dataTransfer.files[0];
                if (file) {
                  const fakeEvent = { target: { files: [file], value: "" } };
                  handleFileChange(fakeEvent);
                }
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
              <div style={{ fontSize: 32, marginBottom: 8 }}>📁</div>
              {importFile ? (
                <div>
                  <div style={{ fontWeight: 700, fontSize: 14, color: "#1e293b" }}>
                    {importFile.name}
                  </div>
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                    {(importFile.size / 1024).toFixed(1)} KB — Click to choose a different file
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: "#334155" }}>
                    Click to select or drag & drop an Excel file
                  </div>
                  <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>
                    Supports .xlsx and .xls (exported employee sheets or standard templates)
                  </div>
                </div>
              )}
            </div>

            {isParsing && (
              <div style={{ textAlign: "center", padding: "10px 0", color: "#4f46e5" }}>
                <span className="spinner-border spinner-border-sm me-2" role="status" />
                Parsing Excel file...
              </div>
            )}

            {/* Action buttons */}
            <div style={{ marginTop: "auto", display: "flex", gap: 10 }}>
              {importFile && (
                <button
                  className="btn"
                  style={{
                    flex: 1,
                    background: "#f1f5f9",
                    color: "#475569",
                    border: "1px solid #cbd5e1",
                    borderRadius: 10,
                    padding: "12px",
                    fontWeight: 600,
                    fontSize: 14,
                    cursor: "pointer",
                  }}
                  onClick={resetImport}
                  disabled={importing}
                >
                  ✕ Clear
                </button>
              )}
              <button
                className="btn"
                style={{
                  flex: 2,
                  background:
                    !importFile || previewData.length === 0 || importing
                      ? "#94a3b8"
                      : "linear-gradient(135deg, #1d4ed8, #3b82f6)",
                  color: "#fff",
                  borderRadius: 10,
                  padding: "12px 18px",
                  fontWeight: 700,
                  fontSize: 14,
                  border: "none",
                  cursor: !importFile || previewData.length === 0 || importing ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  boxShadow:
                    importFile && previewData.length > 0 && !importing
                      ? "0 4px 14px rgba(29,78,216,0.35)"
                      : "none",
                  transition: "all 0.2s",
                }}
                onClick={handleImport}
                disabled={!importFile || previewData.length === 0 || importing}
              >
                {importing ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" />
                    Importing ({importProgress}%)...
                  </>
                ) : (
                  `🚀 Run Import (${previewData.length} rows)`
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ── PREVIEW TABLE ── */}
      {showPreview && previewData.length > 0 && (
        <div
          style={{
            marginTop: 28,
            background: "#fff",
            borderRadius: 16,
            boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
            overflow: "hidden",
            border: "1px solid #e8e8f0",
          }}
        >
          <div
            style={{
              padding: "16px 24px",
              borderBottom: "1px solid #f0f0f5",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: "#fafafa",
            }}
          >
            <div>
              <h5 style={{ margin: 0, fontWeight: 700, fontSize: 16, color: "#1f2937" }}>
                📋 Import Data Preview
              </h5>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: "#6b7280" }}>
                Total: <strong>{previewData.length}</strong> | Valid:{" "}
                <span style={{ color: "#059669", fontWeight: 600 }}>{validRows}</span> | Errors:{" "}
                <span style={{ color: "#dc3545", fontWeight: 600 }}>{invalidRows}</span>
              </p>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button
                className="btn btn-sm"
                style={{
                  background: "#4f46e5",
                  color: "#fff",
                  borderRadius: 6,
                  fontWeight: 600,
                  padding: "6px 16px",
                }}
                onClick={handleImport}
                disabled={importing || validRows === 0}
              >
                {importing ? "Importing..." : `Import ${validRows} Valid Rows`}
              </button>
            </div>
          </div>

          <div style={{ overflowX: "auto", maxHeight: 420 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={thStyle}>#</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Type</th>
                  <th style={thStyle}>Employee Code</th>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Father Name</th>
                  <th style={thStyle}>Gender</th>
                  <th style={thStyle}>DOB</th>
                  <th style={thStyle}>PF App.</th>
                  <th style={thStyle}>ESI App.</th>
                  <th style={thStyle}>Basic Salary</th>
                  <th style={thStyle}>Dynamic Allowances</th>
                  <th style={thStyle}>Validation / Errors</th>
                </tr>
              </thead>
              <tbody>
                {previewData.map((row, idx) => (
                  <tr
                    key={idx}
                    style={{
                      background:
                        row.status === "success"
                          ? "#f0fdf4"
                          : row.status === "failed"
                          ? "#fff1f2"
                          : row.status === "skipped"
                          ? "#fff7ed"
                          : row.status === "error"
                          ? "#fff1f2"
                          : idx % 2 === 0
                          ? "#fff"
                          : "#fafafa",
                      borderBottom: "1px solid #f0f0f5",
                    }}
                  >
                    <td style={tdStyle}>{row.rowIndex}</td>
                    <td style={tdStyle}>
                      <StatusBadge status={row.status} action={row.apiAction} />
                    </td>
                    <td style={tdStyle}>
                      <span
                        style={{
                          background: row.isNew ? "#e0e7ff" : "#fef3c7",
                          color: row.isNew ? "#3730a3" : "#92400e",
                          padding: "2px 6px",
                          borderRadius: 4,
                          fontSize: 10,
                          fontWeight: 700,
                        }}
                      >
                        {row.isNew ? "NEW (INSERT)" : "EXISTING (UPDATE)"}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, fontWeight: 600, color: "#1d4ed8" }}>
                      {row.data.CardNo || <em style={{ color: "#9ca3af" }}>Auto-Gen</em>}
                    </td>
                    <td style={{ ...tdStyle, fontWeight: 600 }}>{row.data.Name}</td>
                    <td style={tdStyle}>{row.data.FatherName || "—"}</td>
                    <td style={tdStyle}>{row.data.Sex || "M"}</td>
                    <td style={tdStyle}>{row.data.DateOfBirth || "—"}</td>
                    <td style={tdStyle}>{row.data.PFApplicable || "—"}</td>
                    <td style={tdStyle}>{row.data.ESIApplicable || "—"}</td>
                    <td style={tdStyle}>{row.data.Sal_BasicSalary || row.data.BasicSalary || "—"}</td>
                    <td style={tdStyle}>
                      {row.data.Allowances?.length > 0 ? (
                        <span style={{ color: "#ea580c", fontWeight: 600 }}>
                          {row.data.Allowances.length} item(s)
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td
                      style={{
                        ...tdStyle,
                        color:
                          row.errors?.length > 0
                            ? "#dc3545"
                            : row.apiError
                            ? "#dc3545"
                            : row.status === "success"
                            ? "#059669"
                            : "#6b7280",
                      }}
                    >
                      {row.errors?.length > 0
                        ? row.errors.join(", ")
                        : row.apiError
                        ? row.apiError
                        : row.status === "success"
                        ? row.apiAction === "INSERTED"
                          ? "✓ Inserted"
                          : "✓ Updated"
                        : "Ready"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── FORMAT GUIDELINES & CODES MODAL ── */}
      {showGuidelinesModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            backdropFilter: "blur(4px)",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowGuidelinesModal(false);
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 20,
              width: "100%",
              maxWidth: 860,
              maxHeight: "90vh",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 24px 64px rgba(0,0,0,0.25)",
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                background: "linear-gradient(135deg, #4f46e5, #7c3aed)",
                padding: "20px 24px",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <h4 style={{ margin: 0, fontWeight: 700, fontSize: 18 }}>
                  📖 Format Guidelines & Code Definitions
                </h4>
                <p style={{ margin: "4px 0 0", opacity: 0.9, fontSize: 13 }}>
                  Cheat sheet of standard codes, acceptable formats, and matching rules
                </p>
              </div>
              <button
                style={smallBtnStyle("#fff", "#666")}
                onClick={() => setShowGuidelinesModal(false)}
              >
                ✕ Close
              </button>
            </div>

            {/* Search Bar */}
            <div
              style={{
                padding: "12px 24px",
                background: "#f9fafb",
                borderBottom: "1px solid #e5e7eb",
                display: "flex",
                gap: 10,
              }}
            >
              <input
                type="text"
                placeholder="🔍 Search guidelines (e.g. Calculation, Gender, Date, PF, Allowance, CardNo)..."
                value={guidelineSearch}
                onChange={(e) => setGuidelineSearch(e.target.value)}
                style={{
                  flex: 1,
                  padding: "8px 14px",
                  borderRadius: 8,
                  border: "1px solid #d1d5db",
                  fontSize: 13,
                  outline: "none",
                }}
              />
              {guidelineSearch && (
                <button
                  onClick={() => setGuidelineSearch("")}
                  style={{
                    background: "#e5e7eb",
                    border: "none",
                    borderRadius: 6,
                    padding: "6px 12px",
                    fontSize: 12,
                    cursor: "pointer",
                  }}
                >
                  Clear
                </button>
              )}
            </div>

            {/* Modal Body */}
            <div style={{ overflowY: "auto", padding: 24, flex: 1 }}>
              {filteredGuidelines.map((cat, idx) => (
                <div key={idx} style={{ marginBottom: 24 }}>
                  <h6
                    style={{
                      fontWeight: 700,
                      fontSize: 14,
                      color: "#4338ca",
                      borderBottom: "2px solid #e0e7ff",
                      paddingBottom: 6,
                      marginBottom: 12,
                    }}
                  >
                    {cat.category}
                  </h6>
                  <div style={{ display: "grid", gap: 12 }}>
                    {cat.fields.map((f, fIdx) => (
                      <div
                        key={fIdx}
                        style={{
                          background: "#f8fafc",
                          border: "1px solid #e2e8f0",
                          borderRadius: 10,
                          padding: "12px 16px",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: 6,
                          }}
                        >
                          <span style={{ fontWeight: 700, fontSize: 13, color: "#1e293b" }}>
                            {f.field}
                          </span>
                          <span
                            style={{
                              background: "#e0e7ff",
                              color: "#4338ca",
                              borderRadius: 6,
                              padding: "2px 8px",
                              fontSize: 11,
                              fontWeight: 600,
                            }}
                          >
                            Format: {f.format}
                          </span>
                        </div>
                        <div style={{ fontSize: 12, color: "#334155", lineHeight: 1.5 }}>
                          <div><strong>Allowed Values / Codes:</strong> <code style={{ background: "#e2e8f0", padding: "1px 6px", borderRadius: 4 }}>{f.allowed}</code></div>
                          <div style={{ marginTop: 4, whiteSpace: "pre-line" }}><strong>Rule:</strong> {f.rule}</div>
                          <div style={{ marginTop: 4, color: "#059669" }}>
                            <strong>Example Value:</strong> <code>{f.example}</code>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Modal Footer */}
            <div
              style={{
                padding: "14px 24px",
                borderTop: "1px solid #f0f0f5",
                display: "flex",
                justifyContent: "flex-end",
                background: "#fafafa",
              }}
            >
              <button
                className="btn"
                style={{
                  background: "linear-gradient(135deg, #4f46e5, #7c3aed)",
                  color: "#fff",
                  borderRadius: 8,
                  padding: "8px 24px",
                  fontWeight: 600,
                  fontSize: 13,
                  border: "none",
                  cursor: "pointer",
                }}
                onClick={() => setShowGuidelinesModal(false)}
              >
                Got It!
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── RESULT MODAL ── */}
      {showResultModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.55)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
            backdropFilter: "blur(4px)",
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 20,
              width: "100%",
              maxWidth: 600,
              boxShadow: "0 24px 64px rgba(0,0,0,0.2)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                background:
                  importResults.failed > 0
                    ? "linear-gradient(135deg, #d97706, #f59e0b)"
                    : "linear-gradient(135deg, #059669, #10b981)",
                padding: "24px",
                color: "#fff",
                textAlign: "center",
              }}
            >
              <div style={{ fontSize: 48, marginBottom: 8 }}>
                {importResults.failed === 0 ? "🎉" : "⚠️"}
              </div>
              <h4 style={{ margin: 0, fontWeight: 700 }}>Import Finished!</h4>
              <p style={{ margin: "6px 0 0", opacity: 0.95, fontSize: 14 }}>
                Batch processed across {importResults.total} total rows
              </p>
            </div>
            <div style={{ padding: 24 }}>
              {/* Stats grid */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 20 }}>
                {[
                  { label: "Total Rows", value: importResults.total, color: "#6b7280", bg: "#f4f4f8" },
                  { label: "Inserted", value: importResults.inserted, color: "#059669", bg: "#f0fdf4" },
                  { label: "Updated", value: importResults.updated, color: "#2563eb", bg: "#eff6ff" },
                  { label: "Failed", value: importResults.failed, color: "#dc3545", bg: "#fff1f2" },
                ].map((s) => (
                  <div
                    key={s.label}
                    style={{
                      background: s.bg,
                      borderRadius: 10,
                      padding: "12px 8px",
                      textAlign: "center",
                    }}
                  >
                    <div style={{ fontSize: 24, fontWeight: 700, color: s.color }}>{s.value}</div>
                    <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2, fontWeight: 600 }}>
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>

              {/* Error details */}
              {importResults.errors.length > 0 && (
                <div
                  style={{
                    background: "#fff8f8",
                    border: "1px solid #fecaca",
                    borderRadius: 10,
                    padding: 14,
                    marginBottom: 16,
                    maxHeight: 160,
                    overflowY: "auto",
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: 13, color: "#dc3545", marginBottom: 8 }}>
                    Error Details:
                  </div>
                  {importResults.errors.map((e, i) => (
                    <div
                      key={i}
                      style={{ fontSize: 12, color: "#6b7280", marginBottom: 4, fontFamily: "monospace" }}
                    >
                      • {e}
                    </div>
                  ))}
                </div>
              )}

              <div style={{ display: "flex", gap: 10 }}>
                <button
                  className="btn"
                  style={{
                    flex: 1,
                    background: "linear-gradient(135deg, #4f46e5, #7c3aed)",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    padding: "12px",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    setShowResultModal(false);
                    resetImport();
                  }}
                >
                  Done
                </button>
                {importResults.success > 0 && (
                  <button
                    className="btn"
                    style={{
                      flex: 1,
                      background: "#f4f4f8",
                      border: "1px solid #e0e0eb",
                      color: "#374151",
                      borderRadius: 8,
                      padding: "12px",
                      fontWeight: 500,
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      setShowResultModal(false);
                      navigate("/EmployeeMaster");
                    }}
                  >
                    View Employee List →
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────
// Style helpers
// ─────────────────────────────────────────────
const thStyle = {
  padding: "10px 12px",
  textAlign: "left",
  fontWeight: 700,
  fontSize: 11,
  color: "#374151",
  whiteSpace: "nowrap",
  borderBottom: "2px solid #e8e8f0",
  background: "#f4f4f8",
};

const tdStyle = {
  padding: "8px 12px",
  fontSize: 12,
  color: "#374151",
  whiteSpace: "nowrap",
  maxWidth: 200,
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const smallBtnStyle = (color, bg) => ({
  background: "rgba(255,255,255,0.15)",
  border: "1px solid rgba(255,255,255,0.35)",
  color: "#fff",
  borderRadius: 6,
  padding: "5px 12px",
  fontSize: 12,
  cursor: "pointer",
  fontWeight: 500,
  whiteSpace: "nowrap",
});

export default EmployeeImportExport;
